type SongDurationLike = {
  length_minutes?: number | null;
  length_seconds?: number | null;
};

type ParsedSongDurationInput = {
  lengthMinutes: number;
  lengthSeconds: number;
};

function toSafeInt(value?: number | null): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.max(0, Math.trunc(value));
}

export function formatSongTemplateLengthDisplay(song?: SongDurationLike | null): string | null {
  if (!song) return null;

  const totalSeconds = toSafeInt(song.length_seconds);

  if (totalSeconds == null) {
    const minutes = toSafeInt(song.length_minutes) ?? 0;
    return minutes > 0 ? `${minutes} min` : null;
  }

  if (totalSeconds === 0) {
    const minutes = toSafeInt(song.length_minutes) ?? 0;
    return minutes > 0 ? `${minutes}:00` : null;
  }

  const normalizedMinutes = Math.floor(totalSeconds / 60);
  const normalizedSeconds = totalSeconds % 60;

  if (normalizedMinutes === 0 && normalizedSeconds === 0) {
    return null;
  }

  return `${normalizedMinutes}:${String(normalizedSeconds).padStart(2, '0')}`;
}

export function getSongTemplateDurationInputValue(song?: SongDurationLike | null): string {
  if (!song) return '';

  const totalSeconds = toSafeInt(song.length_seconds);
  if (totalSeconds != null) {
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
  }

  const minutes = toSafeInt(song.length_minutes);
  return minutes == null ? '' : String(minutes);
}

export function parseSongTemplateDurationInput(rawValue: string): { value: ParsedSongDurationInput | null; error: string | null } {
  const trimmed = rawValue.trim();

  if (!trimmed) {
    return {
      value: null,
      error: 'Duration must be minutes (5) or m:ss (4:53).',
    };
  }

  if (/^\d+$/.test(trimmed)) {
    const minutes = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(minutes) || minutes < 0) {
      return {
        value: null,
        error: 'Duration must be 0 or greater.',
      };
    }

    return {
      value: {
        lengthMinutes: minutes,
        lengthSeconds: minutes * 60,
      },
      error: null,
    };
  }

  const minuteSecond = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (!minuteSecond) {
    return {
      value: null,
      error: 'Duration must be minutes (5) or m:ss (4:53).',
    };
  }

  const minutes = Number.parseInt(minuteSecond[1], 10);
  const seconds = Number.parseInt(minuteSecond[2], 10);
  if (!Number.isFinite(minutes) || minutes < 0 || !Number.isFinite(seconds) || seconds < 0 || seconds > 59) {
    return {
      value: null,
      error: 'Use m:ss with seconds between 00 and 59.',
    };
  }

  return {
    value: {
      lengthMinutes: minutes,
      lengthSeconds: minutes * 60 + seconds,
    },
    error: null,
  };
}